import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { toast } from 'react-hot-toast';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';

export const CrmCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasHandledCallback = useRef(false);
  const {
    handleCrmCallback,
    isLoading: isAuthLoading,
    isAuthenticated,
    selectedProvider,
  } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      logger.debug('CrmCallback', 'Effect triggered', {
        isAuthLoading,
        isAuthenticated,
        hasHandled: hasHandledCallback.current,
        selectedProvider,
      });
      if (isAuthLoading) {
        logger.debug('CrmCallback', 'Firebase auth still loading, waiting...');
        return;
      }

      if (!isAuthenticated) {
        logger.error(
          'CrmCallback',
          'User not authenticated after Firebase auth check. Redirecting to login.'
        );
        toast.error('Authentication session issue. Please log in again.');
        navigate('/login');
        return;
      }

      if (hasHandledCallback.current) {
        logger.debug('CrmCallback', 'Callback already handled, skipping.');
        return;
      }
      hasHandledCallback.current = true;
      logger.debug('CrmCallback', 'Proceeding to handle CRM callback code.');

      const code = searchParams.get('code');
      // Get the provider from the auth store's selectedProvider
      const provider = selectedProvider;

      if (!code) {
        logger.error('CrmCallback', 'No CRM authorization code found in URL.');
        toast.error('Authentication failed.');
        navigate('/login');
        return;
      }

      if (!provider) {
        logger.error('CrmCallback', 'No CRM provider selected.');
        toast.error('No CRM provider selected. Please try again.');
        navigate('/login');
        return;
      }

      logger.info('CrmCallback', 'CRM authorization code received, processing...', { provider });
      try {
        await handleCrmCallback(provider, code);
        logger.info('CrmCallback', 'CRM connection successful. Navigating to dashboard.', {
          provider,
        });
        toast.success(`Successfully connected to ${provider}`);
        navigate('/dashboard');
      } catch (error) {
        logger.error('CrmCallback', 'Error during handleCrmCallback', { error, provider });
        toast.error(
          `Failed to complete ${provider} authentication. Please try again or contact support.`
        );
        navigate('/login');
      }
    };

    if (!isAuthLoading) {
      handleCallback();
    }
  }, [searchParams, navigate, handleCrmCallback, isAuthLoading, isAuthenticated, selectedProvider]);

  return <FullScreenLoader message="Completing CRM connection..." showOverlay={false} />;
};
